import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf } from "@/api";
import ClientDashboardView from "@/components/ClientDashboardView";

export default function AdminPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [overview, monthly, yearly, messages, pricing, opportunities] = await Promise.all([
        api.adminOverview(id),
        api.adminPeriods(id, "monthly"),
        api.adminPeriods(id, "yearly"),
        api.adminMessages(id),
        api.previewPricing(id).catch(() => ({ strategies: [] })),
        api.previewOpportunities(id).catch(() => ({ years: [] })),
      ]);
      setData({ overview, monthly, yearly, messages, pricing: pricing?.strategies || [], opportunities: opportunities?.years || [] });
    } catch (e) {
      setError(e.message || "Errore di caricamento");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onDownloadPdf = async (period) => {
    try {
      await downloadPdf(`/admin/structures/${id}/periods/${period.id}/report.pdf`, `YieldUp-${period.period_key}.pdf`);
    } catch (e) {
      toast.error("Download del report non riuscito");
    }
  };

  const onDownloadPricingPdf = async (year) => {
    try { await downloadPdf(`/admin/structures/${id}/pricing/${year}/pdf`, `YieldUp-Piano-Giornaliero-${year}.pdf`); }
    catch { toast.error("Download non riuscito"); }
  };
  const onDownloadOpportunitiesPdf = async (year) => {
    try { await downloadPdf(`/admin/structures/${id}/opportunities/${year}/pdf`, `YieldUp-Opportunita-${year}.pdf`); }
    catch { toast.error("Download non riuscito"); }
  };
  const onRefreshAir = async (year) => {
    try {
      await api.adminAirRefresh(id, year);
      const opportunities = await api.previewOpportunities(id).catch(() => ({ years: [] }));
      setData((d) => ({ ...d, opportunities: opportunities?.years || [] }));
      toast.success("Check voli aggiornato");
    } catch (e) { toast.error(e.message || "Aggiornamento voli non riuscito"); }
  };

  return (
    <div className="theme-brand min-h-screen" style={{ background: "#12100E" }}>
      {/* Orange preview banner */}
      <div className="sticky top-0 z-30 flex items-center justify-between px-5 md:px-6 py-3"
        style={{ background: "#E05A2C", color: "#fff" }}>
        <div className="inline-flex items-center gap-2 text-sm font-medium">
          <Eye className="h-4 w-4" /> Anteprima cliente — stai vedendo la dashboard come la vede il cliente
        </div>
        <button onClick={() => navigate(`/admin/structures/${id}`)}
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium"
          style={{ background: "rgba(0,0,0,0.2)" }}>
          <ArrowLeft className="h-4 w-4" /> Torna alla scheda
        </button>
      </div>

      {loading ? (
        <div className="max-w-5xl mx-auto px-6 py-20 eyebrow" style={{ color: "#857C72" }}>Caricamento…</div>
      ) : error ? (
        <div className="max-w-5xl mx-auto px-6 py-20" style={{ color: "#C4BBB0" }}>{error}</div>
      ) : (
        <ClientDashboardView
          overview={data.overview}
          monthly={data.monthly}
          yearly={data.yearly}
          messages={data.messages}
          pricing={data.pricing}
          opportunities={data.opportunities}
          onSendMessage={null}
          onDownloadPdf={onDownloadPdf}
          onDownloadPricingPdf={onDownloadPricingPdf}
          onDownloadOpportunitiesPdf={onDownloadOpportunitiesPdf}
          onPricingFeedback={null}
          onRefreshAir={onRefreshAir}
        />
      )}
    </div>
  );
}
